import React, { useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";
import "./ReactMediaPlayer.css";
import fullscreenIcon from "./assets/fullscreen.svg";
import playIcon from "./assets/play.svg";
import pauseIcon from "./assets/pause.svg";
import volumeIcon from "./assets/volume.svg";
import muteIcon from "./assets/mute.svg";
import youtubeIcon from "./assets/youtube.svg";

const ReactMediaPlayer = ({ url }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
  const [volume, setVolume] = useState(0.8);
  const [isUserActive, setIsUserActive] = useState(true);
  const [userInteracting, setUserInteracting] = useState(false);
  const [lastTouchTime, setLastTouchTime] = useState(0);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [lastClickTime, setLastClickTime] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  const playerRef = useRef(null);
  const wrapperRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const userActivityTimeoutRef = useRef(null);
  
  // Path to your local video file
  const videoSource = url || "/videos/be.mp4"; 
  // YouTube video ID for the YouTube button
  const youtubeVideoId = "RGSGfFddqes";
  
  useEffect(() => {
    // Check if user is on mobile device
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|iPad|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };
    
    checkMobile();
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Initial timeout to hide controls after load
    resetControlsTimeout();
    
    // Listen for document-level fullscreenchange event
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Center video content when entering fullscreen
      if (!!document.fullscreenElement) {
        setTimeout(() => {
          const videoElement = playerRef.current?.getInternalPlayer();
          if (videoElement) {
            videoElement.style.objectFit = 'contain';
          }
        }, 100);
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // Track user activity
    startUserActivityTimer();
    
    return () => {
      clearTimeout(controlsTimeoutRef.current);
      clearTimeout(userActivityTimeoutRef.current);
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset controls timeout - always hide after 2.5s
  const resetControlsTimeout = () => {
    clearTimeout(controlsTimeoutRef.current);
    
    // Only set a timeout to hide controls if the video is playing
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false); // Always hide if timer fires during playback
      }, 2500);
    }
  };

  // Handle when user is interacting with controls
  const startInteraction = (e) => {
    // Stop event propagation to prevent play/pause when clicking controls
    e.stopPropagation();
    setUserInteracting(true);
    clearTimeout(controlsTimeoutRef.current);
  };
  
  const endInteraction = () => {
    setUserInteracting(false);
    resetControlsTimeout();
  };

  // Start the user activity timer
  const startUserActivityTimer = () => {
    clearTimeout(userActivityTimeoutRef.current);
    setIsUserActive(true); // isUserActive state can still be used if needed elsewhere
    
    userActivityTimeoutRef.current = setTimeout(() => {
      setIsUserActive(false);
      // Removed setShowControls(false) from here to prevent conflict
      // if (isPlaying && !userInteracting) {
      //   setShowControls(false);
      // }
    }, 3000);
  };

  // Handle mouse movement
  const handleMouseMove = () => {
    startUserActivityTimer();
  };

  // Handle touch start events
  const handleTouchStart = (e) => {
    // Store the position of the touch start
    const touch = e.touches[0];
    setTouchStartPos({ 
      x: touch.clientX, 
      y: touch.clientY 
    });
    
    // Show controls on first touch
    setShowControls(true);
    startUserActivityTimer();
    resetControlsTimeout();
  };
  
  // Handle touch end events for play/pause on tap
  const handleTouchEnd = (e) => {
    const now = Date.now();
    // Threshold for what counts as a "tap" vs a "swipe" (in pixels)
    const MOVE_THRESHOLD = 10;
    
    // Only register as a tap if it's not a long press and not a swipe
    if (now - lastTouchTime < 300) {
      return; // Double tap too close together - likely meant for fullscreen
    }
    
    // Only trigger play/pause if there was minimal movement (not a swipe)
    if (e.changedTouches && e.changedTouches.length > 0) {
      const touch = e.changedTouches[0];
      const diffX = Math.abs(touch.clientX - touchStartPos.x);
      const diffY = Math.abs(touch.clientY - touchStartPos.y);
      
      // If it was a tap (not much movement) and not on a control element
      if (diffX < MOVE_THRESHOLD && diffY < MOVE_THRESHOLD) {
        // Check if the target is not a control button or slider
        const isControlElement = 
          e.target.closest('.control-button') || 
          e.target.closest('.progress-bar') || 
          e.target.closest('.volume-slider') ||
          e.target.closest('.youtube-button') ||
          e.target.closest('.media-controls');
        
        if (!isControlElement) {
          // Toggle play state
          handlePlayPause(e);
          // We're no longer in initial load state after user interaction
          setInitialLoad(false);
        }
      }
    }
    
    setLastTouchTime(now);
  };

  // Handle click for play/pause anywhere on video
  const handleVideoClick = (e) => {
    const now = Date.now();
    const DOUBLE_CLICK_THRESHOLD = 300;
    
    // Don't play/pause on double click (used for fullscreen)
    if (now - lastClickTime < DOUBLE_CLICK_THRESHOLD) {
      // Reset and let the double click handler take it
      setLastClickTime(now);
      return;
    }
    
    // Check if the target is not a control element
    const isControlElement = 
      e.target.closest('.control-button') || 
      e.target.closest('.progress-bar') || 
      e.target.closest('.volume-slider') ||
      e.target.closest('.youtube-button') ||
      e.target.closest('.media-controls');
    
    if (!isControlElement) {
      // Toggle play state
      handlePlayPause(e);
      // We're no longer in initial load state after user interaction
      setInitialLoad(false);
    }
    
    setLastClickTime(now);
  };

  // Force hide controls when playing
  useEffect(() => {
    if (isPlaying) {
      // When play state changes to playing, ensure we hide controls after timeout
      resetControlsTimeout();
    } else {
      // When paused, always show controls and clear any hide timeout
      clearTimeout(controlsTimeoutRef.current);
      setShowControls(true);
    }

    // This makes sure the controls are re-rendered when isPlaying changes
    // causing the button icons to update on mobile
    if (showControls) {
      // Force a re-render by toggling showControls
      setShowControls(false);
      setTimeout(() => setShowControls(true), 10);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);
  
  // Effect to hide cursor when controls are hidden and video is playing
  useEffect(() => {
    const playerElement = wrapperRef.current;
    if (playerElement) {
      if (!showControls && isPlaying) {
        playerElement.classList.add('no-cursor');
      } else {
        playerElement.classList.remove('no-cursor');
      }
    }
  }, [showControls, isPlaying]);
  
  // Format time to MM:SS
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const handlePlayPause = (e) => {
    if (e) {
      e.stopPropagation();
    }
    // Update playing state immediately
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    
    // For mobile, ensure controls are immediately visible when we pause
    if (!newPlayingState) {
      setShowControls(true);
    }
    
    resetControlsTimeout();
    // No longer in initial state after play/pause
    setInitialLoad(false);
  };
  
  const handleVolumeToggle = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsMuted(!isMuted);
    resetControlsTimeout();
  };
  
  const handleVolumeChange = (e) => {
    e.stopPropagation();
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
    resetControlsTimeout();
  };
  
  const handleFullscreen = (e) => {
    if (e) {
      e.stopPropagation();
    }
    const player = wrapperRef.current;
    if (!player) return;

    if (!document.fullscreenElement) {
      if (player.requestFullscreen) {
        player.requestFullscreen();
      } else if (player.webkitRequestFullscreen) { /* Safari */
        player.webkitRequestFullscreen();
      } else if (player.msRequestFullscreen) { /* IE11 */
        player.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) { /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) { /* IE11 */
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
    resetControlsTimeout();
  };
  
  const handleSeek = (e) => {
    e.stopPropagation();
    const seekPosition = parseFloat(e.target.value);
    setCurrentTime(seekPosition);
    if (playerRef.current) {
      playerRef.current.seekTo(seekPosition, 'seconds');
    }
    resetControlsTimeout();
  };
  
  // Handle orientation change
  const handleOrientationChange = () => { 
    // Reset fullscreen styling for better display in landscape
    setTimeout(() => {
      const videoElement = playerRef.current?.getInternalPlayer();
      if (videoElement) {
        videoElement.style.objectFit = 'contain';
      }
      
      // Automatically go fullscreen in landscape if not already
      if ((window.orientation === 90 || window.orientation === -90) && !document.fullscreenElement) {
        const player = wrapperRef.current;
        if (player) {
          handleFullscreen();
        }
      }
    }, 300); // Small delay to ensure orientation has completed
  };
  
  const handleYoutubeRedirect = (e) => {
    e.stopPropagation();
    window.open(`https://www.youtube.com/watch?v=${youtubeVideoId}`, '_blank');
    resetControlsTimeout();
  };
  
  const handlePlayerHover = (e) => {
    // Check if mouse has actually moved significantly from last position
    const currentPosition = { x: e.clientX, y: e.clientY };
    const distance = Math.sqrt(
      Math.pow(currentPosition.x - lastMousePosition.x, 2) + 
      Math.pow(currentPosition.y - lastMousePosition.y, 2)
    );
    setLastMousePosition(currentPosition);

    if (isPlaying) { // Only proceed if video is playing
      if (distance > 1) { // Only if mouse genuinely moved (threshold > 1 pixel)
        if (!showControls) { // If controls are currently hidden
          setShowControls(true); // Then show them
        }
        // Whether controls were just shown or already visible, 
        // if mouse moves during playback, reset the hide timer.
        resetControlsTimeout(); 
      }
      // If distance <= 1 (e.g., tap-generated mousemove with no real movement),
      // we don't re-show controls if they were hidden, and don't reset timer needlessly.
    }
    
    // startUserActivityTimer is for the general isUserActive state,
    // which doesn't directly control visibility of controls anymore.
    startUserActivityTimer();
  };
  
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    handleFullscreen();
  };
  
  const handleProgress = (progress) => {
    setCurrentTime(progress.playedSeconds);
  };
  
  const handleDuration = (duration) => {
    setDuration(duration);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle keyboard events when the player is in focus or fullscreen
      if (wrapperRef.current && (wrapperRef.current.contains(document.activeElement) || isFullscreen)) {
        switch (e.key) {
          case ' ':
            e.preventDefault();
            handlePlayPause();
            break;
          case 'f':
            e.preventDefault();
            handleFullscreen();
            break;
          case 'm':
            e.preventDefault();
            handleVolumeToggle();
            break;
          default:
            break;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentTime, duration, isFullscreen]);
  
  // Determine if we should show the big play button
  // Only show on initial load on desktop, never on mobile
  const shouldShowBigPlayButton = !isPlaying && initialLoad && !isMobile;
  
  // Create current play/pause icon variable to ensure it's always in sync
  const currentIcon = isPlaying ? pauseIcon : playIcon;
  const currentIconAlt = isPlaying ? "Pause" : "Play";
  
  return (
    <div 
      className={`media-player-container ${isFullscreen ? 'fullscreen' : ''}`}
      ref={wrapperRef}
      onMouseMove={handlePlayerHover}
      onMouseMoveCapture={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleVideoClick}
      onDoubleClick={handleDoubleClick}
      tabIndex="0"
    >
      <div className="video-wrapper">
        <ReactPlayer
          ref={playerRef}
          className="video-element"
          url={videoSource}
          width="100%"
          height="100%"
          playing={isPlaying}
          volume={volume}
          muted={isMuted}
          playsinline
          onProgress={handleProgress}
          onDuration={handleDuration}
          progressInterval={200}
          config={{
            file: {
              attributes: {
                poster: "/images/video-poster.jpg"
              },
              forceVideo: true
            }
          }}
        />
        
        {/* Gradient overlay for better control visibility */}
        <div className="gradient-overlay"></div>
        
        {/* Large play button in center - only shown on initial load for desktop */}
        {shouldShowBigPlayButton && (
          <button 
            className="center-play-button" 
            onClick={handlePlayPause}
            aria-label="Play video"
          >
            <div className="play-icon">▶</div>
          </button>
        )}
        
        {/* Video controls */}
        <div 
          className={`media-controls ${showControls ? 'visible' : 'hidden'}`}
          onMouseEnter={(e) => startInteraction(e)}
          onMouseLeave={endInteraction}
          onTouchStart={(e) => startInteraction(e)}
          onTouchEnd={(e) => {
            // Don't end interaction immediately on touch so controls stay visible
            e.stopPropagation();
          }}
        >
          <div className="progress-container">
            <input
              type="range"
              className="progress-bar"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              aria-label="Video progress"
            />
            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
          
          <div className="controls-bottom">
            <div className="left-controls">
              <button className="control-button" onClick={handlePlayPause} aria-label={currentIconAlt}>
                <img src={currentIcon} alt={currentIconAlt} />
              </button>
              
              <button className="control-button" onClick={handleVolumeToggle} aria-label={isMuted ? "Unmute" : "Mute"}>
                <img src={isMuted ? muteIcon : volumeIcon} alt={isMuted ? "Unmute" : "Mute"} />
              </button>
              
              <input
                type="range"
                className="volume-slider"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label="Volume control"
              />
              
              <div className="time-display mobile-hidden">
                <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
              </div>
            </div>
            
            <div className="right-controls">
              <button 
                className="youtube-button"
                onClick={handleYoutubeRedirect}
                aria-label="Watch on YouTube"
              >
                <img src={youtubeIcon} alt="YouTube" />
                <span>YouTube</span>
              </button>
              
              <button 
                className="control-button" 
                onClick={handleFullscreen}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                <img src={fullscreenIcon} alt={isFullscreen ? "Exit fullscreen" : "Fullscreen"} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactMediaPlayer; 